import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Printer, Save, CheckCircle, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';

export type ChecklistItem = { id: string; text: string; category?: string; required?: boolean };

type Template = { id: string; title: string; type: string; items: string; requiresSignoff: boolean };
type Existing = { id: string; data: string; status: string; signedOff: boolean };
type Props = {
  template: Template;
  existing?: Existing;
  onSave: (d: { completionId?: string; data: object; status: string; signedOff: boolean }) => void;
  onBack: () => void;
  userName: string;
};

export default function ChecklistRunner({ template, existing, onSave, onBack, userName }: Props) {
  const items: ChecklistItem[] = useMemo(() => {
    try { return JSON.parse(template.items) || []; } catch { return []; }
  }, [template.items]);

  const initialChecked = useMemo(() => {
    if (!existing?.data) return new Set<string>();
    try { return new Set<string>(JSON.parse(existing.data).checked || []); } catch { return new Set<string>(); }
  }, [existing]);

  const [checked, setChecked] = useState<Set<string>>(initialChecked);
  const [notes, setNotes] = useState('');
  const [signedOff, setSignedOff] = useState(existing?.signedOff || false);

  const toggle = (id: string) =>
    setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const progress = items.length > 0 ? (checked.size / items.length) * 100 : 0;
  const allDone = items.length > 0 && checked.size === items.length;

  const grouped = useMemo(() => {
    const map: Record<string, ChecklistItem[]> = {};
    items.forEach(item => {
      const cat = item.category || 'General';
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    return map;
  }, [items]);

  const submit = (status: string) =>
    onSave({ completionId: existing?.id, data: { checked: Array.from(checked), notes }, status, signedOff });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold">{template.title}</h2>
          <p className="text-sm text-muted-foreground">{template.type} — {format(new Date(), 'dd MMM yyyy')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{checked.size} of {items.length} items</span>
          <span className="font-semibold">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{cat}</h3>
            <div className="space-y-1.5">
              {catItems.map(item => (
                <div key={item.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${checked.has(item.id) ? 'bg-green-500/5 border-green-500/20' : 'border-border hover:bg-muted/40'}`}
                  onClick={() => toggle(item.id)}>
                  <Checkbox id={item.id} checked={checked.has(item.id)} onCheckedChange={() => toggle(item.id)} className="mt-0.5" />
                  <label htmlFor={item.id} className={`text-sm cursor-pointer flex-1 ${checked.has(item.id) ? 'line-through text-muted-foreground' : ''}`}>
                    {item.text}{item.required && <span className="ml-1 text-destructive text-xs">*</span>}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <Label>Notes / Comments</Label>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any notes or observations..." className="mt-1" rows={3} />
      </div>

      {template.requiresSignoff && (
        <div className={`p-4 rounded-xl border ${signedOff ? 'bg-green-500/5 border-green-500/20' : 'border-border'}`}>
          <div className="flex items-center gap-3">
            <Checkbox id="signoff" checked={signedOff} onCheckedChange={(c) => setSignedOff(!!c)} />
            <label htmlFor="signoff" className="text-sm cursor-pointer">
              Manager sign-off — I confirm this checklist was completed satisfactorily by {userName}
            </label>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={() => submit('In Progress')} className="flex-1"><Save className="w-4 h-4 mr-1.5" /> Save Progress</Button>
        <Button onClick={() => submit(allDone ? (signedOff ? 'Signed Off' : 'Completed') : 'In Progress')} disabled={!allDone} className="flex-1">
          <CheckCircle className="w-4 h-4 mr-1.5" /> Complete
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
